import type { JSX } from 'react';

import { alpha } from '@theme-ui/color';

import { Translate, TranslateHtml } from '@change-corgi/core/react/i18n';
import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import { iconSentimentSatisfiedAlt, iconSentimentVeryDissatisfied } from '@change-corgi/design-system-legacy/icons';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { useIsSunriseTheme } from '@change-corgi/design-system-legacy/theme';
import { Text } from '@change-corgi/design-system-legacy/typography';

import type { DecisionMaker } from 'src/app/pages/petitionGamma/details/shared/types';

import type { AllDmResponses } from '../../../../hooks/useDecisionMakerCards';
import { useDmResponse } from '../../shared/hooks/useDmResponse';

import { NotifiedSince } from './NotifiedSince';

function RespondedStatus({ isSunriseThemeEnabled }: { isSunriseThemeEnabled: boolean }) {
	return (
		<Flex
			data-testid="dm-status-responded"
			sx={{
				background: isSunriseThemeEnabled ? 'sunrise.semantic.fill.success.weaker' : alpha('social-whatsapp', 0.2),
				borderRadius: isSunriseThemeEnabled ? '16px' : 'standard',
				alignItems: 'center',
			}}
			px={isSunriseThemeEnabled ? 12 : 8}
			py={4}
		>
			{!isSunriseThemeEnabled && <Icon icon={iconSentimentSatisfiedAlt} mr={8} />}
			<Text size={isSunriseThemeEnabled ? 'default' : 'small'}>
				<Translate value="corgi.petition_gamma.decision_makers.response_status.responded" />
			</Text>
		</Flex>
	);
}

function NotRespondedStatus({
	isSunriseThemeEnabled,
	decisionMaker,
}: {
	isSunriseThemeEnabled: boolean;
	decisionMaker: DecisionMaker;
}) {
	return (
		<Flex
			data-testid="dm-status-not-responded"
			sx={{
				background: isSunriseThemeEnabled ? 'sunrise.semantic.fill.inverse.weak' : '#FFE5E5',
				borderRadius: isSunriseThemeEnabled ? '16px' : 'standard',
				alignItems: 'center',
			}}
			px={isSunriseThemeEnabled ? 12 : 8}
			py={4}
		>
			{!isSunriseThemeEnabled && <Icon icon={iconSentimentVeryDissatisfied} mr={8} />}
			<Box>
				<Text size={isSunriseThemeEnabled ? 'default' : 'small'} as="p" fontWeight="normal">
					<TranslateHtml value="corgi.petition_gamma.decision_makers.response_status.not_responded" />{' '}
					<NotifiedSince daysSinceNotified={decisionMaker.targetPetitionAssociationData?.daysSinceNotified} />
				</Text>
			</Box>
		</Flex>
	);
}

type DmResponseStatusProps = {
	decisionMaker: DecisionMaker;
	allDmResponses: AllDmResponses;
};

export function DmResponseStatus({ decisionMaker, allDmResponses }: DmResponseStatusProps): JSX.Element {
	const {
		data: { dmHasResponded },
	} = useDmResponse({ decisionMaker, allDmResponses });
	const isSunriseThemeEnabled = useIsSunriseTheme();

	return (
		<Box data-testid="dm-response-status" mb={8}>
			{dmHasResponded ? (
				<RespondedStatus isSunriseThemeEnabled={isSunriseThemeEnabled} />
			) : (
				<NotRespondedStatus isSunriseThemeEnabled={isSunriseThemeEnabled} decisionMaker={decisionMaker} />
			)}
		</Box>
	);
}
